import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'
import {
  roleAssignmentsGridContract,
  rolesGridContract,
  useDataGrid,
} from '@/modules/data-grid'
import {
  rolesApi,
  type CreateEntityRoleInput,
} from '@/modules/iam/api/roles-api'
import { roleKeys, iamInvalidates, assistantEffectMeta } from '../../api/query-keys'

export const permissionCatalogKey = (entityId: string) =>
  ['permissions', 'catalog', entityId] as const

const ROLE_WRITE_ERRORS: Record<string, string> = {
  ROLE_NAME_TAKEN:
    'A role with that name already exists in this managed subtree.',
  ROLE_ESCALATION:
    'You can only include permissions you already hold at this entity.',
  UNKNOWN_PERMISSION: 'One of the selected permissions isn’t recognized.',
}

const UPDATE_ROLE_ERRORS: Record<string, string> = {
  ...ROLE_WRITE_ERRORS,
  SYSTEM_ROLE_READ_ONLY: 'Built-in roles can’t be edited.',
}

export function createRoleErrorMessage(error: unknown): string {
  const mapped =
    error instanceof ApiError && error.code
      ? ROLE_WRITE_ERRORS[error.code]
      : undefined
  if (mapped) return mapped
  if (error instanceof ApiError && error.status === 403)
    return 'Custom roles can only be created at the user management entity.'
  return apiErrorMessage(error, 'Couldn’t create the role. Try again.', {
    codes: ROLE_WRITE_ERRORS,
  })
}

export function updateRoleErrorMessage(error: unknown): string {
  const mapped =
    error instanceof ApiError && error.code
      ? UPDATE_ROLE_ERRORS[error.code]
      : undefined
  if (mapped) return mapped
  if (error instanceof ApiError && error.status === 403)
    return 'Custom roles can only be edited at the user management entity.'
  return apiErrorMessage(error, 'Couldn’t save the role. Try again.', {
    codes: UPDATE_ROLE_ERRORS,
  })
}

export function useRolesGrid(entityId: string | null) {
  return useDataGrid({
    contract: rolesGridContract,
    queryKey: [...roleKeys.list(entityId ?? ''), 'grid'],
    endpoint: `/api/v1/entities/${entityId ?? ''}/roles`,
    enabled: entityId !== null,
    meta: assistantEffectMeta,
  })
}

export function useRoleDetail(entityId: string | null, roleId: string) {
  return useQuery({
    queryKey: roleKeys.detail(entityId ?? '', roleId),
    queryFn:
      entityId === null ? skipToken : () => rolesApi.getRoleDetail(entityId, roleId),
    meta: assistantEffectMeta,
  })
}

export function useRoleAssignmentsGrid(
  entityId: string | null,
  roleId: string | null,
) {
  return useDataGrid({
    contract: roleAssignmentsGridContract,
    queryKey: [...roleKeys.detail(entityId ?? '', roleId ?? ''), 'assignments', 'grid'],
    endpoint: `/api/v1/entities/${entityId ?? ''}/roles/${roleId ?? ''}/assignments`,
    enabled: entityId !== null && roleId !== null,
    meta: assistantEffectMeta,
  })
}

export function useAssignableRoles(
  entityId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: roleKeys.assignable(entityId),
    queryFn: () => rolesApi.getEntityRoles(entityId),
    enabled: options?.enabled ?? true,
    meta: assistantEffectMeta,
  })
}

export function usePermissionCatalog(
  entityId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: permissionCatalogKey(entityId),
    queryFn: () => rolesApi.getPermissionCatalog(entityId),
    enabled: options?.enabled ?? true,
  })
}

export function useCreateRole(entityId: string) {
  return useMutation({
    mutationFn: (input: CreateEntityRoleInput) =>
      rolesApi.createEntityRole(entityId, input),
    meta: { invalidates: [roleKeys.root] },
  })
}

export function useUpdateRole(entityId: string, roleId: string) {
  return useMutation({
    mutationFn: (input: CreateEntityRoleInput) =>
      rolesApi.updateEntityRole(entityId, roleId, input),
    meta: { invalidates: iamInvalidates() },
  })
}

export function useDeleteRole(entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (roleId: string) => rolesApi.deleteEntityRole(entityId, roleId),
    meta: { invalidates: iamInvalidates() },
    onSuccess: (_data, roleId) =>
      queryClient.removeQueries({ queryKey: roleKeys.detail(entityId, roleId) }),
  })
}
